/**
 * FGA User model interfaces
 *
 * These interfaces define the structure of user entities in Auth0 FGA.
 * Users are identified with the format: user:{provider}|{organisationId}|{userId}
 *
 * @packageDocumentation
 */

/**
 * Supported identity providers for FGA users.
 * - waad: Windows Azure Active Directory (70% typical)
 * - auth0: Auth0 database users (20% typical)
 * - google-oauth2: Google OAuth users (10% typical)
 */
export type IdentityProvider = 'waad' | 'auth0' | 'google-oauth2';

/**
 * Represents an FGA user entity.
 * Maps to FGA user identifier: `user:{provider}|{organisationId}|{userId}`
 */
export interface FgaUser {
  /**
   * Full FGA user identifier
   * @example "user:waad|org_demo|abc123-def456"
   */
  id: string;

  /**
   * Display name (parsed from ID or fetched from metadata)
   */
  name: string;

  /**
   * Identity provider that authenticated this user (optional)
   */
  provider?: IdentityProvider;

  /**
   * Organisation this user belongs to (optional)
   */
  organisationId?: string;

  /**
   * User's email address (if available)
   */
  email?: string;

  /**
   * Group IDs this user is a member of
   */
  groups?: string[];

  /**
   * Number of groups the user belongs to
   */
  groupCount?: number;

  /**
   * Indicates if async operations are in progress for this user
   */
  loading?: boolean;

  /**
   * Creation timestamp (if available)
   */
  createdAt?: string;

  /**
   * Last modification timestamp
   */
  updatedAt?: string;
}

/**
 * Response from listing users for an organisation.
 */
export interface FgaUsersResponse {
  /**
   * Array of user identifiers (full FGA format)
   */
  users: string[];

  /**
   * Organisation the users belong to
   */
  organisationId: string;

  /**
   * Pagination token for retrieving additional results
   */
  continuationToken?: string;
}

/**
 * Response containing groups that a specific user belongs to.
 */
export interface FgaUserGroupsResponse {
  /**
   * User identifier
   */
  userId: string;

  /**
   * Array of group identifiers the user is a member of
   */
  groups: string[];

  /**
   * Pagination token for retrieving additional results
   */
  continuationToken?: string;
}

/**
 * Parsed components from an FGA user identifier.
 */
export interface ParsedUserId {
  /**
   * Original full ID string (with user: prefix)
   */
  fullId: string;

  /**
   * User identifier without the user: prefix
   */
  userId: string;
}

/**
 * Parses a user ID string into its component parts.
 *
 * @param userId - Full FGA user identifier (e.g., 'user:waad|org_demo|abc123')
 * @returns Parsed user ID components containing fullId and userId
 * @throws Error if userId format is invalid (missing 'user:' prefix)
 *
 * @example
 * ```typescript
 * const parsed = parseUserId('user:waad|org_demo|abc123');
 * // Returns: {
 * //   fullId: 'user:waad|org_demo|abc123',
 * //   userId: 'waad|org_demo|abc123'
 * // }
 * ```
 */
export function parseUserId(userId: string): ParsedUserId {
  if (!userId.startsWith('user:')) {
    throw new Error(`Invalid user ID format: ${userId}`);
  }
  return {
    fullId: userId,
    userId: userId.substring(5) // Remove 'user:' prefix
  };
}

/**
 * Creates a full FGA user identifier from components.
 *
 * @param provider - Identity provider
 * @param organisationId - Organisation ID
 * @param userId - User identifier within provider
 * @returns Full FGA user identifier
 *
 * @example
 * ```typescript
 * const id = buildUserId('waad', 'org_demo', 'abc123');
 * // Returns: 'user:waad|org_demo|abc123'
 * ```
 */
export function buildUserId(
  provider: IdentityProvider,
  organisationId: string,
  userId: string
): string {
  return `user:${provider}|${organisationId}|${userId}`;
}
